'use strict';

import * as THREE from 'three';
import { Buildings, isMobile, projectMode, settingsData, threeJsVariables } from '../../settings';
import {
    autoRotateTimerAction,
    getCurrentCameraDistance, isOrbitControlsEnabled,
    setMinAndMaxControlsDistance,
} from './orbitControls';
import {ViewMode} from "../../common";
import { createHtmlElement, userPrevent } from '../../functions';
import {getHighlights, getSelectionsByName} from '../threejs/autoSelectionBoxes'
import {stopCameraTour} from "../camera-auto-tour/cameraTour";
import { Vector3 } from 'three';
import {LiteMode} from "../mode/liteMode";

let timerID, touchStartX, touchStartY;

export function zoomButtonsControl(parent) {
    if (isMobile) {
        return;
    }

    const mainWrapper = createHtmlElement(parent, '', 'div', ['zoom__controls']);
    const decreaseBtn = createHtmlElement(mainWrapper, '', 'button', ['zoom__controls__btn', 'icon-minus']);
    decreaseBtn.dataset.zoomBtn = 'decrease';
    const increaseBtn = createHtmlElement(mainWrapper, '', 'button', ['zoom__controls__btn', 'icon-plus']);
    increaseBtn.dataset.zoomBtn = 'increase';

    mainWrapper.addEventListener('click', zoomControlsHandler);
    mainWrapper.addEventListener('mousedown', holdingInButton);
    mainWrapper.addEventListener('mouseleave', holdingOutButton);
    mainWrapper.addEventListener('mouseup', holdingOutButton);

    mainWrapper.addEventListener('touchstart', holdingInButton);
    mainWrapper.addEventListener('touchmove', holdingOutButton);
    mainWrapper.addEventListener('touchend', holdingOutButton);
}

function holdingInButton(e) {
    if (settingsData.tourIsActive) {
        stopCameraTour();
        return;
    }

    if (!isOrbitControlsEnabled()) return;

    if (e.type === 'touchstart'){
        touchStartX = e.touches[0].clientX;
        touchStartY = e.touches[0].clientY;
    }
    e.preventDefault();
    const controls = threeJsVariables.trackballControls;
    const zoomStep = settingsData.zoomButtonsStep;

    function timer() {
        if (e.target.classList.contains('disabled')){
            holdingOutButton(e);
            return;
        }
        // console.log("Timer tick:");
        switch (e.target.dataset.zoomBtn) {
            case 'decrease':
                !e.target.classList.contains('disabled') && controls.zoomCamera(zoomStep.decrease);
                break;
            case 'increase':
                !e.target.classList.contains('disabled') && controls.zoomCamera(zoomStep.increase);
                break;
        }

        setDisableToZoomButtons();

        timerID = requestAnimationFrame(timer);
    }

    requestAnimationFrame(timer);
}

function holdingOutButton(e) {
    if (settingsData.tourIsActive) {
        stopCameraTour();
        return;
    }
    if (e.type === 'touchmove'){
        const moveX = e.touches[0].clientX;
        const moveY = e.touches[0].clientY;

        if (Math.abs(touchStartX - moveX) > 20 || Math.abs(touchStartY - moveY) > 20){
            cancelAnimationFrame(timerID);
        }
    } else {
        cancelAnimationFrame(timerID);
    }
}

function zoomControlsHandler({target}) {
    if (settingsData.tourIsActive) {
        stopCameraTour();
        return;
    }

    if (userPrevent()){
        return;
    }

    if (projectMode.LiteMode.isActive() && projectMode.LiteMode.isVisible()){

        projectMode.LiteMode.zoomListener(target);
        return;
    }

    autoRotateTimerAction('update');

    const controls = threeJsVariables.trackballControls;
    const zoomStep = settingsData.zoomButtonsStep;

    switch (target.dataset.zoomBtn) {
        case 'decrease':
            !target.classList.contains('disabled') && controls.zoomCamera(zoomStep.decrease);
            break;
        case 'increase':
            !target.classList.contains('disabled') && controls.zoomCamera(zoomStep.increase);
            break;
    }

    setDisableToZoomButtons();
}

export async function fitModelInViewport(setDistance = false, model) {

    if (settingsData.buildingDestroyed) return;

    const zoomSettings = config.threejs.orbitControls.autoZoomSettings;
    if (!zoomSettings.activate) return;

    let size;

    if (model) {
        if (model.isVector3) {
            size = model;
        } else {
            const box = new THREE.Box3().setFromObject(model);
            size = box.getSize(new THREE.Vector3());
        }

    } else {
        size = await getTotalSizeOfBuildingAndSelectionBoxes();
        // totalSize.then((vectorSize) => size = vectorSize);
    }

    // console.log(size);
    // const boxHelp = new Box3Helper(box,0xffff00);
    // threeJsVariables.scene.add(boxHelp);

    let scalar;
    let foreshorteningZoomCorrection;
    let minZoomCorrection;

    if (isMobile){

        if (settingsData.isLandscape){
            scalar = zoomSettings.mobile.landscape.scale;
            foreshorteningZoomCorrection = zoomSettings.mobile.landscape.foreshorteningZoomCorrection;
            minZoomCorrection = zoomSettings.mobile.landscape.minZoomCorrection;
        } else {
            scalar = zoomSettings.mobile.portrait.scale;
            foreshorteningZoomCorrection = zoomSettings.mobile.portrait.foreshorteningZoomCorrection;
            minZoomCorrection = zoomSettings.mobile.portrait.minZoomCorrection;
        }

    } else {
        scalar = zoomSettings.desktop.scale;
        foreshorteningZoomCorrection = zoomSettings.desktop.foreshorteningZoomCorrection;
        minZoomCorrection = zoomSettings.desktop.minZoomCorrection;
    }


    size.multiplyScalar(scalar);

    const maxDim = Math.max(size.x, size.y, size.z);
    const h = size.y;

    const cameraFov = threeJsVariables.perspectiveCamera.fov;
    const fovX = cameraFov * (window.innerWidth / window.innerHeight);
    // let d = Math.radians(45/2) * size.y / 2 / (80/100) + size.x / 2;
    // console.log(d);
    const distanceX = (maxDim / 2) / Math.tan(Math.PI * fovX / 360) + (maxDim / 2);
    const distanceY = (h / 2) / Math.tan(Math.PI * cameraFov / 360) + (maxDim / 2);

    const distance = Math.max(distanceX, distanceY);

    zoomSettings.max = distance;
    zoomSettings.foreshortening = distance / foreshorteningZoomCorrection;
    zoomSettings.min = distance / minZoomCorrection;

    // setMaxAndMinDistanceFromViewport(distance, viewMode);
    setDistance && setMinAndMaxControlsDistance();

    // settingsData.globalSettings.animations.defaultForeshortening.zoom = distance;

    return {
        size,
        distance
    };
}

export function getZoomParams () {
    const zoom = config.threejs.orbitControls.autoZoomSettings;
    return {
        min: zoom.min,
        max: zoom.max,
        foreshortening: zoom.foreshortening
    }
}

export function autoZoomIsActive () {
    return config.threejs.orbitControls.autoZoomSettings.activate;
}

export function getTotalSizeOfBuildingAndSelectionBoxes() {
    let buildings, sBoxes;
    return new Promise(resolve => {
        const checkModels = setInterval(() => {
            if (settingsData.activeViewMode !== 'orbit') {
                // const activeBuilding = buildings_info.find(building => building.isActive);
                const activeBuilding = Buildings.getActiveBuilding();
                buildings = threeJsVariables.scene.getObjectByName(activeBuilding.name);
                sBoxes = activeBuilding.selectionsGroup;
            } else {
                buildings = threeJsVariables.scene.getObjectByName('Project Buildings');
                sBoxes = getHighlights(); // threeJsVariables.scene.getObjectByName('Project Selection Boxes');
            }

            // const box1 = new THREE.BoxHelper( buildings, 0x000000 );
            // const box2 = new THREE.BoxHelper( sBoxes, 0xffff00 );
            // threeJsVariables.scene.add(box1,box2);
            if (buildings && sBoxes) {
                clearInterval(checkModels);
                const buildingSize = new THREE.Box3().setFromObject(buildings).getSize(new THREE.Vector3());
                const sBoxSize = new THREE.Box3().setFromObject(sBoxes).getSize(new THREE.Vector3());
                const totalSize = new THREE.Vector3(buildingSize.x, sBoxSize.y, buildingSize.z);
                resolve(totalSize);
            } else if (!buildings && !sBoxes && settingsData.loadingError) {
                clearInterval(checkModels);
                resolve(new Vector3(40,40,40));
            }
        }, 100);
    });
}

export function setZoomOnWindowResize() {
    return;
    switch (settingsData.activeViewMode) {
        case ViewMode.Orbit:
            const totalBuildingSize = getTotalSizeOfBuildingAndSelectionBoxes();
            totalBuildingSize.then((vectorSize) => {
                const zoomDistance = fitModelInViewport(vectorSize);
                // settingsData.target_zoom = zoomDistance.distance;
            });
            break;

        case ViewMode.Cylinder:
            // const currentActiveBuilding = buildings_info.find(building => building.isActive);
            const currentActiveBuilding = Buildings.getActiveBuilding();
            const buildingSelectBox = threeJsVariables.scene.getObjectByName(currentActiveBuilding.name + '_selectionBoxes');
            const cylinderDist = fitModelInViewport(buildingSelectBox);
            settingsData.target_zoom = cylinderDist.distance;
            break;
    }
}

export function fitModelAndSetMaxMinProperties(model, viewMode = settingsData.activeViewMode) {
    return;
    return new Promise(resolve => {
        if (model) {
          const {size, distance} = fitModelInViewport(model, viewMode);
          // setMaxMinCameraProperties(size,distance,changeDefaultPositionY);
          resolve();
        } else {
          const totalSize = getTotalSizeOfBuildingAndSelectionBoxes();
          totalSize.then((vectorSize) => {
            const {size, distance} = fitModelInViewport(vectorSize, viewMode);
            // setMaxMinCameraProperties(size,distance,changeDefaultPositionY);
            resolve();
          });
        }
    });
}

export const setDisableToZoomButtons = () => {
  const currentZoom = getCurrentCameraDistance();
  const buttonIncrease = document.querySelector('.zoom__controls__btn.icon-plus');
  const buttonDecrease = document.querySelector('.zoom__controls__btn.icon-minus');

  if (!buttonIncrease || !buttonDecrease){
      return;
  }

  const minDistance = threeJsVariables.orbitControls.minDistance;
  const maxDistance = threeJsVariables.orbitControls.maxDistance;

    if (currentZoom >= maxDistance - 2) {
        !buttonDecrease.classList.contains('disabled') && buttonDecrease.classList.add('disabled');
    } else if (currentZoom <= minDistance + 2 ) {
        !buttonIncrease.classList.contains('disabled') && buttonIncrease.classList.add('disabled');
    } else {
        buttonIncrease.classList.contains('disabled') && buttonIncrease.classList.remove('disabled');
        buttonDecrease.classList.contains('disabled') && buttonDecrease.classList.remove('disabled');
    }
}